import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { useToast } from "../hooks/use-toast";
import { Send } from "lucide-react";
import { submitLead } from "../utils/api";

const ContactForm = ({ compact = false, type = "contact", initialMessage = "" }) => {
  const { toast } = useToast();
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    intent: "",
    firstTime: "",
    preApproved: "",
    needLender: "",
    message: initialMessage,
    subscribe: false,
  });

  useEffect(() => {
    if (initialMessage) {
      setForm((f) => ({
        ...f,
        message: initialMessage,
        intent: f.intent || "buying",
      }));
    }
  }, [initialMessage]);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.intent) {
      toast({
        title: "A few fields still need you",
        description: "Please fill in your name, email, phone and what you're planning.",
      });
      return;
    }
    setSubmitting(true);
    try {
      await submitLead({ ...form, type, page: location.pathname });
      toast({
        title: "Thank you — I'll be in touch soon!",
        description: `Got it, ${form.name.split(" ")[0]}. I personally review every message.`,
      });
      setForm({
        name: "",
        email: "",
        phone: "",
        intent: "",
        firstTime: "",
        preApproved: "",
        needLender: "",
        message: "",
        subscribe: false,
      });
    } catch (err) {
      toast({
        title: "Hmm, that didn't send",
        description: "Please try again in a moment, or call me directly at 210-274-3556.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className={`grid grid-cols-1 ${compact ? "" : "md:grid-cols-2"} gap-5`}>
        <div className="space-y-2">
          <Label className="text-[#4a423a] text-xs tracking-wide uppercase">Name *</Label>
          <Input
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder="Your full name"
            className="bg-white border-[#e0d6c5] focus-visible:ring-[#d4af37] h-11"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-[#4a423a] text-xs tracking-wide uppercase">Email *</Label>
          <Input
            type="email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            placeholder="you@email.com"
            className="bg-white border-[#e0d6c5] focus-visible:ring-[#d4af37] h-11"
          />
        </div>
      </div>

      <div className={`grid grid-cols-1 ${compact ? "" : "md:grid-cols-2"} gap-5`}>
        <div className="space-y-2">
          <Label className="text-[#4a423a] text-xs tracking-wide uppercase">Phone *</Label>
          <Input
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            placeholder="(210) 000-0000"
            className="bg-white border-[#e0d6c5] focus-visible:ring-[#d4af37] h-11"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-[#4a423a] text-xs tracking-wide uppercase">
            Buying, Selling or Both? *
          </Label>
          <Select value={form.intent} onValueChange={(v) => update("intent", v)}>
            <SelectTrigger className="bg-white border-[#e0d6c5] focus:ring-[#d4af37] h-11">
              <SelectValue placeholder="Choose one" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="buying">Buying</SelectItem>
              <SelectItem value="selling">Selling</SelectItem>
              <SelectItem value="both">Both</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="space-y-2">
          <Label className="text-[#4a423a] text-xs tracking-wide uppercase">First-time buyer?</Label>
          <Select value={form.firstTime} onValueChange={(v) => update("firstTime", v)}>
            <SelectTrigger className="bg-white border-[#e0d6c5] focus:ring-[#d4af37] h-11">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="yes">Yes</SelectItem>
              <SelectItem value="no">No</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-[#4a423a] text-xs tracking-wide uppercase">Pre-approved?</Label>
          <Select value={form.preApproved} onValueChange={(v) => update("preApproved", v)}>
            <SelectTrigger className="bg-white border-[#e0d6c5] focus:ring-[#d4af37] h-11">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="yes">Yes</SelectItem>
              <SelectItem value="no">Not yet</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-[#4a423a] text-xs tracking-wide uppercase">Need a lender?</Label>
          <Select value={form.needLender} onValueChange={(v) => update("needLender", v)}>
            <SelectTrigger className="bg-white border-[#e0d6c5] focus:ring-[#d4af37] h-11">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="yes">Yes, please connect me</SelectItem>
              <SelectItem value="no">I have one</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-[#4a423a] text-xs tracking-wide uppercase">Anything you'd like me to know?</Label>
        <Textarea
          value={form.message}
          onChange={(e) => update("message", e.target.value)}
          placeholder="Share your story — where you are, what you're hoping for..."
          className="bg-white border-[#e0d6c5] focus-visible:ring-[#d4af37] min-h-[110px]"
        />
      </div>

      <div className="flex items-start gap-3">
        <Checkbox
          id="subscribe"
          checked={form.subscribe}
          onCheckedChange={(v) => update("subscribe", !!v)}
          className="mt-0.5 data-[state=checked]:bg-[#d4af37] data-[state=checked]:border-[#d4af37]"
        />
        <Label htmlFor="subscribe" className="text-sm text-[#6b6154] leading-relaxed font-normal">
          Sign me up for updates, new listings, and Corridor market tips.
        </Label>
      </div>

      <Button
        type="submit"
        disabled={submitting}
        className="w-full md:w-auto bg-[#d4af37] hover:bg-[#c19a2e] text-white rounded-full px-10 h-12 tracking-luxury uppercase text-xs"
      >
        <Send className="w-4 h-4 mr-2" /> {submitting ? "Sending..." : "Send Message"}
      </Button>
    </form>
  );
};

export default ContactForm;
